import { Component, Suspense, useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Loader2, Save, X } from "lucide-react";
import { renderAsync } from "docx-preview";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { RegeneratePopover } from "@/components/shared/RegeneratePopover";
import { SuperDocRegeneratePopover } from "@/components/shared/SuperDocRegeneratePopover";
import { ViewerZoomToolbar } from "@/components/shared/ViewerZoomToolbar";
import { invalidateDocxBytes, useFetchDocxBytes } from "@/hooks/useFetchDocxBytes";
import { useFitToWidthZoom } from "@/hooks/useFitToWidthZoom";
import { api } from "@/lib/api";
import { applyTarget } from "@/lib/docTarget";
import type { SaveEditorVersionHandler, SuperDocInstance } from "@/lib/superdoc-types";
import { useResponsiveToolbar } from "@/hooks/useResponsiveToolbar";
import { isSuperDocEnabled } from "@/lib/docxViewerMode";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { markComponentMount, startTimer } from "@/lib/perf";
import type { CitationObject, EditAnnotation } from "@/lib/stream";
import { tagTrackedChanges } from "@/lib/docxTrackedChanges";
import { cn } from "@/lib/utils";

const LazySuperDocxView = lazyWithRetry(() =>
  import("@/components/shared/SuperDocxView").then((m) => ({ default: m.SuperDocxView })),
);

/**
 * Scrollbar allowance (px) kept free when fitting to width. Small because the
 * DOCX path scales the whole host with CSS `zoom`, so the only chrome around the
 * page is the scroll container's scrollbar.
 */
const STAGE_INSET = 8;

/**
 * Measure the rendered page width (including the wrapper's horizontal padding).
 * The render effect forces `host.style.zoom = "1"` before `renderAsync`, so the
 * page is at natural scale when this runs and the fit-to-width math is stable.
 */
function measureNaturalWidth(host: HTMLElement) {
  const page = host.querySelector<HTMLElement>("section.vadium-docx");
  if (!page) {
    return 0;
  }
  const wrapper = host.querySelector<HTMLElement>(".vadium-docx-wrapper");
  const padding = wrapper ? Number.parseFloat(getComputedStyle(wrapper).paddingLeft) + Number.parseFloat(getComputedStyle(wrapper).paddingRight) : 0;
  return page.offsetWidth + (Number.isFinite(padding) ? padding : 0);
}

interface SuperDocErrorBoundaryProps {
  onError: () => void;
  children: ReactNode;
}

interface SuperDocErrorBoundaryState {
  hasError: boolean;
}

class SuperDocErrorBoundary extends Component<SuperDocErrorBoundaryProps, SuperDocErrorBoundaryState> {
  constructor(props: SuperDocErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): SuperDocErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch() {
    this.props.onError();
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

type DocumentMode = "editing" | "suggesting" | "viewing";

interface DocxViewProps {
  documentId: string;
  versionId?: string;
  filename?: string;
  documentMode?: DocumentMode;
  citation?: CitationObject;
  edit?: EditAnnotation;
}

export function DocxView({ documentId, versionId, filename, documentMode, citation, edit }: DocxViewProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const naturalWidthRef = useRef(0);
  const queryClient = useQueryClient();
  const [ready, setReady] = useState(false);
  const [superDocFailed, setSuperDocFailed] = useState(false);
  const [superDocInstance, setSuperDocInstance] = useState<SuperDocInstance | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { bytes, loading, error } = useFetchDocxBytes(documentId, versionId);
  const editorFilename = filename ?? "document.docx";

  const useSuperDoc = isSuperDocEnabled() && !superDocFailed;

  // Responsive toolbar: hide items that overflow the available width.
  useResponsiveToolbar(scrollRef, useSuperDoc && ready);

  const { zoom, fitMode, fitToWidth, setManualZoom, resetZoom, applyFitZoom } = useFitToWidthZoom({
    scrollRef,
    naturalWidthRef,
    stageInset: STAGE_INSET,
    refitOnResize: !useSuperDoc && ready,
  });

  useEffect(() => {
    markComponentMount("DocxView");
  }, []);

  useEffect(() => {
    setDirty(false);
    setSaving(false);
    setSaveError(null);
    setSuperDocInstance(null);
  }, [documentId, versionId]);

  // docx-preview render path — only active when SuperDoc is not in use.
  useEffect(() => {
    const container = containerRef.current;
    if (useSuperDoc || !container || !bytes) {
      return;
    }

    let cancelled = false;
    setReady(false);
    resetZoom();
    // Force the host to natural scale synchronously so the post-render width
    // measurement is taken at zoom=1 (React's setZoom above only schedules a
    // commit). The loading overlay masks this until fit-to-width is applied.
    container.style.zoom = "1";
    naturalWidthRef.current = 0;
    container.replaceChildren();
    const renderTimer = startTimer("docx.render", { documentId, versionId });

    void renderAsync(bytes.slice(0), container, undefined, {
      className: "vadium-docx",
      inWrapper: true,
      ignoreWidth: false,
      ignoreHeight: false,
      renderChanges: true
    })
      .then(async () => {
        if (cancelled) {
          renderTimer.stop({ cancelled: true });
          return;
        }
        renderTimer.stop();
        naturalWidthRef.current = measureNaturalWidth(container);
        try {
          const { ids } = await import("@/lib/api").then(({ api }) => api.documents.trackedChangeIds(documentId, versionId));
          if (!cancelled) {
            tagTrackedChanges(container, ids);
          }
        } catch {
          // The viewer still renders even if change-id tagging is unavailable.
        }
        if (!cancelled) {
          applyFitZoom();
          setReady(true);
        }
      })
      .catch((renderError) => {
        renderTimer.stop({ error: true });
        if (!cancelled) {
          const message = document.createElement("p");
          message.className = "p-4 text-sm text-destructive";
          message.textContent = renderError instanceof Error ? renderError.message : "Could not render DOCX.";
          container.replaceChildren(message);
          setReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [applyFitZoom, bytes, documentId, resetZoom, useSuperDoc, versionId]);

  // Highlight/scroll to the citation or tracked change once content is ready.
  // docx-preview only: `applyTarget` mutates the rendered static DOM.
  useEffect(() => {
    const container = containerRef.current;
    if (useSuperDoc || !ready || !container) {
      return;
    }
    applyTarget(container, scrollRef.current, citation?.quote, edit);
  }, [citation?.quote, edit, ready, useSuperDoc]);

  const handleRegenerated = useCallback(() => {
    invalidateDocxBytes(documentId);
  }, [documentId]);

  const handleSuperDocReady = useCallback(() => {
    setReady(true);
  }, []);

  const handleSuperDocInstance = useCallback((instance: SuperDocInstance | null) => {
    setSuperDocInstance(instance);
  }, []);

  const handleSuperDocDirtyChange = useCallback((nextDirty: boolean) => {
    setDirty(nextDirty);
    if (nextDirty) {
      setSaveError(null);
    }
  }, []);

  const handleSaveEditorVersion: SaveEditorVersionHandler = useCallback(async ({ force = false }: { force?: boolean } = {}) => {
    if (!superDocInstance || saving || (!dirty && !force)) {
      return { status: "skipped" };
    }
    setSaving(true);
    setSaveError(null);
    try {
      // isFinalDoc: true would silently accept all pending tracked changes in the
      // exported blob (see #368) — saved versions must preserve them as pending.
      const blob = await superDocInstance.export({ isFinalDoc: false });
      await api.documents.saveEditorVersion(documentId, blob, editorFilename);
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ["docUrl", documentId] });
      return { status: "saved" };
    } catch (err) {
      const error = err instanceof Error ? err.message : "Could not save editor version.";
      setSaveError(error);
      return { status: "failed", error };
    } finally {
      setSaving(false);
    }
  }, [dirty, documentId, editorFilename, queryClient, saving, superDocInstance]);

  const handleSuperDocFailed = useCallback(() => {
    setSuperDocFailed(true);
    setReady(false);
    setSuperDocInstance(null);
  }, []);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return <div className="h-full overflow-auto p-4 text-sm text-destructive">{error}</div>;
  }

  return (
    <div className="relative flex h-full min-h-0 w-full min-w-0 flex-col">
      {useSuperDoc && (
        <div className="flex items-center justify-end gap-2 border-b bg-card px-3 py-1.5">
          <Button
            type="button"
            size="sm"
            className="gap-1.5"
            disabled={!dirty || saving || !superDocInstance}
            aria-label={saving ? "Saving…" : "Save document"}
            aria-busy={saving}
            onClick={() => void handleSaveEditorVersion()}
          >
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Save
          </Button>
        </div>
      )}
      {saveError ? (
        <div className="flex items-center justify-between gap-2 border-b border-t border-destructive/30 bg-card px-3 py-1.5 text-xs text-destructive">
          <p className="min-w-0 flex-1">{saveError}</p>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            aria-label="Dismiss"
            onClick={() => setSaveError(null)}
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : null}
      {!useSuperDoc && <ViewerZoomToolbar zoom={zoom} fitMode={fitMode} disabled={!ready} onManualZoom={setManualZoom} onFitToWidth={fitToWidth} />}
      <div ref={scrollRef} className={cn("relative min-h-0 min-w-0 flex-1 bg-muted", useSuperDoc ? "overflow-auto" : "hide-scrollbar overflow-auto")}>
        {useSuperDoc && bytes ? (
          <SuperDocErrorBoundary onError={handleSuperDocFailed}>
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="size-6 animate-spin text-primary" />
                </div>
              }
            >
              <LazySuperDocxView
                bytes={bytes}
                documentId={documentId}
                versionId={versionId}
                documentMode={documentMode}
                citation={citation}
                edit={edit}
                onReady={handleSuperDocReady}
                onFailed={handleSuperDocFailed}
                onInstance={handleSuperDocInstance}
                onDirtyChange={handleSuperDocDirtyChange}
              />
            </Suspense>
          </SuperDocErrorBoundary>
        ) : (
          <div ref={containerRef} className="prose-document text-foreground mx-auto w-max" style={{ zoom }} />
        )}
        {ready && !useSuperDoc ? (
          <RegeneratePopover
            documentId={documentId}
            containerRef={containerRef}
            onRegenerated={handleRegenerated}
          />
        ) : null}
        {ready && useSuperDoc && superDocInstance ? (
          <SuperDocRegeneratePopover
            superdoc={superDocInstance}
            documentId={documentId}
            saveDocument={handleSaveEditorVersion}
          />
        ) : null}
      </div>
      {ready ? null : (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      )}
    </div>
  );
}
